import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { TriangleAlertIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ConfirmationDialog, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useUpdateSetPhoneNumber } from "@/hooks/updateSets/useUpdateSetPhoneNumber";
import { misc } from "@/lib/misc";

const updatePhoneNumberSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, "Required")
    .regex(/^\d{10}$/, "Must be 10 digits"),
});

const getNationalPhoneNumber = (phoneNumber: string) => {
  const digits = phoneNumber.replace(/\D+/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
};

export const UpdatePhoneNumberDialog = ({
  currentPhoneNumber,
  onOpenChange,
  open,
  userId,
}: {
  currentPhoneNumber: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updatePhoneNumber, isPending } = useUpdateSetPhoneNumber();

  /* React Hooks */
  const [phoneNumberForConfirmation, setPhoneNumberForConfirmation] =
    useState<string | null>(null);
  const form = useForm<z.infer<typeof updatePhoneNumberSchema>>({
    defaultValues: {
      phoneNumber: getNationalPhoneNumber(currentPhoneNumber),
    },
    resolver: zodResolver(updatePhoneNumberSchema),
  });

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  /* Handlers */
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      form.reset({
        phoneNumber: getNationalPhoneNumber(currentPhoneNumber),
      });
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = ({
    phoneNumber,
  }: z.infer<typeof updatePhoneNumberSchema>) => {
    setPhoneNumberForConfirmation(`1${phoneNumber}`);
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (!phoneNumberForConfirmation) return;

    updatePhoneNumber(
      { phoneNumber: phoneNumberForConfirmation, userId },
      {
        onError: (error) => {
          errorToast({ description: error.message });
        },
        onSuccess: () => {
          successToast({ description: "Phone number updated successfully." });
          setPhoneNumberForConfirmation(null);
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <>
        <Dialog onOpenChange={handleOpenChange} open={open}>
          <DialogContent className="flex flex-col gap-8">
            <DialogHeader className="flex flex-col gap-4">
              <DialogTitle className="text-2xl font-semibold text-slate-700">
                Update phone number
              </DialogTitle>
              <DialogDescription className="!m-0 text-base font-medium text-slate-700">
                Enter the candidate&apos;s new phone number. The +1 country code is
                already set.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form
                className="flex flex-col gap-6"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSubmit={form.handleSubmit(handleSubmit)}
              >
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone number</FormLabel>
                      <FormControl>
                        <Row className="items-center rounded-md border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-slate-950 focus-within:ring-offset-2">
                          <Text className="self-stretch border-r border-slate-200 bg-slate-50 px-3 py-2 font-semibold text-slate-700">
                            +1
                          </Text>
                          <Input
                            {...field}
                            className="border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                            inputMode="numeric"
                            maxLength={10}
                            onPaste={(event) => {
                              event.preventDefault();
                              field.onChange(
                                getNationalPhoneNumber(
                                  misc.formatPhoneNumberOnPaste(
                                    event.clipboardData.getData("text"),
                                  ),
                                ),
                              );
                            }}
                            placeholder="XXX XXX XXXX"
                            type="tel"
                          />
                        </Row>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button
                    className="rounded-full"
                    onClick={() => {
                      handleOpenChange(false);
                    }}
                    type="button"
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button className="rounded-full" type="submit">
                    Continue
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <ConfirmationDialog
          confirmationButtonVariant="destructive"
          confirmButtonText="Update phone number"
          description="Confirm the candidate's new phone number before continuing."
          isLoading={isPending}
          onConfirm={handleConfirm}
          onOpenChange={(nextOpen) => {
            if (!nextOpen && !isPending) setPhoneNumberForConfirmation(null);
          }}
          open={phoneNumberForConfirmation !== null}
          title="Confirm phone number change"
        >
          <Row className="items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <Column className="gap-1">
              <Text className="font-semibold text-red-700">Important</Text>
              <Text className="text-base font-semibold text-red-700">
                {phoneNumberForConfirmation
                  ? `+1 ${misc.formatPhoneNumberOnCopy(phoneNumberForConfirmation)}`
                  : ""}
              </Text>
              <Text className="font-medium text-red-700">
                This changes how the candidate logs into Laborup. Only continue
                if you are confident the phone number is correct.
              </Text>
            </Column>
          </Row>
        </ConfirmationDialog>
      </>
    );
  };

  return render();
};
